// LQC-2170: multi-format expiry duration ("2d 6h 45m") for Time to Expiry.
// The store keeps expiryTimeValue as TOTAL SECONDS; these helpers convert
// between that and the user-facing d/h/m/s string.

export const EXPIRY_MIN_SECONDS = 30; // 30s
export const EXPIRY_MAX_SECONDS = 7 * 24 * 60 * 60; // 7d

// Every outgoing expiry pairs the value with a period type, and the store keeps
// total seconds, so it is always "s" (New/Edit panel and the blotter's
// edit-expiry popover, LQC-2366).
export const EXPIRY_PERIOD_TYPE_SECONDS = "s";

// What a trade starts on at New (15m), and the fallback when a payload needs a
// period the user never chose (GTC/Day, where the backend stamps its own).
export const DEFAULT_EXPIRY_PERIOD_SECONDS = 900;

const UNIT_SECONDS: Record<string, number> = {
  d: 86400,
  h: 3600,
  m: 60,
  s: 1,
};

// Parse a duration string like "2d 6h 45m" (units d/h/m/s, case-insensitive,
// spaces optional, each unit at most once) into total seconds.
// Returns null for anything that doesn't fully match the format.
export function parseExpiryDuration(input: string): number | null {
  const trimmed = input.trim().toLowerCase();
  if (!/^(\d+\s*[dhms]\s*)+$/.test(trimmed)) return null;
  const seenUnits = new Set<string>();
  let totalSeconds = 0;
  for (const [, digits, unit] of trimmed.matchAll(/(\d+)\s*([dhms])/g)) {
    if (seenUnits.has(unit)) return null;
    seenUnits.add(unit);
    totalSeconds += Number(digits) * UNIT_SECONDS[unit];
  }
  return totalSeconds;
}

// Why an entered duration was rejected. Drives the inline message next to the
// input (LQC-2170) - the value is not committed for any of these.
export type ExpiryRejectionReason = "format" | "belowMin" | "aboveMax";

export const EXPIRY_REJECTION_MESSAGES: Record<ExpiryRejectionReason, string> =
  {
    format: "Use the format 2d 6h 45m",
    belowMin: "Minimum is 30s",
    aboveMax: "Maximum is 7d",
  };

// Validate a typed duration. Returns the total seconds when acceptable, or the
// reason it was rejected.
export function validateExpiryDuration(
  input: string,
): { seconds: number; reason?: never } | { seconds?: never; reason: ExpiryRejectionReason } {
  const totalSeconds = parseExpiryDuration(input);
  if (totalSeconds === null) return { reason: "format" };
  if (totalSeconds < EXPIRY_MIN_SECONDS) return { reason: "belowMin" };
  if (totalSeconds > EXPIRY_MAX_SECONDS) return { reason: "aboveMax" };
  return { seconds: totalSeconds };
}

// Format total seconds back to the duration string, omitting zero units
// (e.g. 900 -> "15m", 110700 -> "1d 6h 45m"). Empty string for no value.
export function formatExpirySeconds(totalSeconds: number | undefined): string {
  if (totalSeconds === undefined || totalSeconds <= 0) return "";
  let rest = Math.floor(totalSeconds);
  const parts: string[] = [];
  for (const [unit, unitSeconds] of Object.entries(UNIT_SECONDS)) {
    const count = Math.floor(rest / unitSeconds);
    rest %= unitSeconds;
    if (count > 0) parts.push(`${count}${unit}`);
  }
  return parts.join(" ");
}

// LQC-2173: countdown format for the blotter's Expires column. Capped at two
// units and zero-padded on the trailing one, so the text does not change width
// as it ticks (`4m 07s`, not `4m 7s`).
//   withSeconds  -> "4m 07s" / "47s"
//   without      -> "5h 35m" / "12m"
// Negative input clamps to zero; the caller decides what "expired" reads as.
export function formatExpiryCountdown(
  totalSeconds: number,
  withSeconds: boolean,
): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const pad = (value: number) => String(value).padStart(2, "0");

  if (withSeconds) {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return minutes > 0 ? `${minutes}m ${pad(remainder)}s` : `${remainder}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${pad(minutes)}m` : `${minutes}m`;
}

// Convert a legacy/API (expiryPeriod, expiryPeriodType) pair to total seconds,
// e.g. (15, "m") -> 900. Used when hydrating from the Edit Settings response.
// Unknown/missing period types fall back to minutes (the legacy default).
const PERIOD_TYPE_SECONDS: Record<string, number> = {
  s: 1,
  second: 1,
  seconds: 1,
  m: 60,
  min: 60,
  mins: 60,
  minute: 60,
  minutes: 60,
  h: 3600,
  hour: 3600,
  hours: 3600,
  d: 86400,
  day: 86400,
  days: 86400,
};

export function convertExpiryPeriodToSeconds(
  period: number | undefined,
  periodType: string | undefined,
): number | undefined {
  if (period === undefined) return undefined;
  const multiplier = PERIOD_TYPE_SECONDS[periodType?.toLowerCase() ?? ""] ?? 60;
  return period * multiplier;
}
